import { useState } from 'react'
import { Alert, App as AntdApp, Button, Form, Input, Select, Space, Tag, Typography } from 'antd'
import { LockOutlined, LoginOutlined, UserOutlined } from '@ant-design/icons'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

const { Text, Title } = Typography

const ROLE_OPTIONS = [
  { value: 'service_agent', label: '接待' },
  { value: 'tech_owner', label: '技术' },
  { value: 'manager', label: '经理' },
  { value: 'admin', label: '管理员' },
  { value: 'auditor', label: '审计' },
]

interface LoginFormValues {
  actor?: string
  roles?: string[]
  apiToken?: string
  password?: string
}

function redirectPath(state: unknown) {
  if (state && typeof state === 'object' && 'from' in state) {
    const from = (state as { from?: unknown }).from
    if (typeof from === 'string' && from.startsWith('/') && from !== '/login') {
      return from
    }
  }
  return '/dashboard'
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<LoginFormValues>()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const {
    actor,
    roles,
    authMode,
    authLoading,
    isAuthenticated,
    mustChangePassword,
    loginWithDevHeader,
    loginWithPassword,
    loginWithToken,
  } = useAuthStore()
  const from = redirectPath(location.state)

  if (isAuthenticated) {
    if (mustChangePassword) return <Navigate to="/account/password" replace />
    return <Navigate to={from} replace />
  }

  const submit = async () => {
    const values = await form.validateFields()
    setErrorMessage(null)
    try {
      if (authMode === 'token') {
        await loginWithToken(values.apiToken || '')
      } else if (authMode === 'password') {
        await loginWithPassword(values.actor || '', values.password || '')
      } else {
        await loginWithDevHeader(values.actor || '', values.roles || [])
      }
      message.success('登录成功')
      const nextPath = useAuthStore.getState().mustChangePassword ? '/account/password' : from
      navigate(nextPath, { replace: true })
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '登录失败')
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: '#f5f5f5',
      padding: 24,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 28,
      }}>
        <Space orientation="vertical" size={20} style={{ width: '100%' }}>
          <Space orientation="vertical" size={4}>
            <Space>
              <Title level={3} style={{ margin: 0 }}>售后工单操作台</Title>
              {authMode === 'dev_header' && <Tag color="orange">开发模式</Tag>}
              {authMode === 'password' && <Tag color="green">密码登录</Tag>}
            </Space>
            <Text type="secondary">请登录后继续使用系统。</Text>
          </Space>

          {errorMessage && (
            <Alert type="error" showIcon message={errorMessage} />
          )}

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              actor: actor || import.meta.env.VITE_DEV_ACTOR || 'ops_admin',
              roles: roles.length > 0 ? roles : ['admin', 'service_agent'],
            }}
            onFinish={submit}
          >
            {authMode === 'token' ? (
              <Form.Item
                name="apiToken"
                label="API Token"
                rules={[{ required: true, message: '请输入 API Token' }]}
              >
                <Input.Password
                  autoComplete="current-password"
                  prefix={<LockOutlined />}
                  placeholder="输入 X-API-Token"
                />
              </Form.Item>
            ) : authMode === 'password' ? (
              <>
                <Form.Item
                  name="actor"
                  label="账号"
                  rules={[{ required: true, message: '请输入账号' }]}
                >
                  <Input autoComplete="username" prefix={<UserOutlined />} />
                </Form.Item>
                <Form.Item
                  name="password"
                  label="密码"
                  rules={[{ required: true, message: '请输入密码' }]}
                >
                  <Input.Password
                    autoComplete="current-password"
                    prefix={<LockOutlined />}
                    placeholder="输入登录密码"
                  />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  name="actor"
                  label="账号"
                  rules={[{ required: true, message: '请输入账号' }]}
                >
                  <Input autoComplete="username" prefix={<UserOutlined />} />
                </Form.Item>
                <Form.Item
                  name="roles"
                  label="角色"
                  rules={[{ required: true, message: '请选择至少一个角色' }]}
                >
                  <Select
                    mode="multiple"
                    options={ROLE_OPTIONS}
                    placeholder="选择本地开发角色"
                  />
                </Form.Item>
              </>
            )}
            <Button
              block
              htmlType="submit"
              icon={<LoginOutlined />}
              loading={authLoading}
              type="primary"
            >
              登录
            </Button>
          </Form>
        </Space>
      </div>
    </div>
  )
}
